import { gql, useQuery } from '@apollo/client';
import { Alert } from '@apollo/orbit';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { useInternalGraphLinking } from '../hooks/useInternalGraphLinking';

export const RerunBannerCheckFragment = gql`
  fragment RerunBannerCheckFragment on CheckWorkflow {
    id
    baseVariant {
      id
      name
    }
  }
`;

function RerunLink({
  rerun,
  children,
}: {
  rerun: GraphQLTypes.RerunBannerCheckFragment;
  children: string;
}) {
  const { currentCheckRoute } = useInternalGraphLinking();
  const location = useLocation();
  return (
    <ClickableText
      className="font-semibold underline"
      as={
        <Link
          to={currentCheckRoute.locationFrom({
            location,
            patch: {
              checkId: rerun.id,
              graphVariant: rerun.baseVariant?.name,
            },
          })}
        />
      }
    >
      {children}
    </ClickableText>
  );
}
export function RerunBanner({
  graphId,
  checkId,
  rerunOfCheck,
  className,
}: {
  graphId: string;
  checkId: string;
  rerunOfCheck: GraphQLTypes.RerunBannerCheckFragment | null;
  className?: string;
}) {
  const { data: rerunData } = useQuery<
    GraphQLTypes.RerunBannerQuery,
    GraphQLTypes.RerunBannerQueryVariables
  >(
    gql`
      query RerunBannerQuery($graphId: ID!, $checkId: ID!) {
        service(id: $graphId) {
          id
          checkWorkflow(id: $checkId) {
            id
            upstreamWorkflow {
              id
              reruns(limit: 1) {
                id
                ...RerunBannerCheckFragment
              }
            }
            reruns(limit: 1) {
              id
              ...RerunBannerCheckFragment
            }
          }
        }
      }
      ${RerunBannerCheckFragment}
    `,
    {
      pollInterval: 30_000,
      variables: { graphId, checkId },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );
  const workflow = rerunData?.service?.checkWorkflow;
  const newestRerun =
    workflow?.reruns?.[0] ?? workflow?.upstreamWorkflow?.reruns?.[0];

  if (newestRerun?.id && rerunOfCheck) {
    return (
      <Alert status="info" className={className}>
        This check is a rerun of a previous check and has a newer version
        available,{' '}
        <RerunLink rerun={newestRerun}>view the latest version</RerunLink> or{' '}
        <RerunLink rerun={rerunOfCheck}>previous check</RerunLink>.
      </Alert>
    );
  } else if (newestRerun?.id) {
    return (
      <Alert status="info" className={className}>
        A new version of this check is available,{' '}
        <RerunLink rerun={newestRerun}>view the latest version</RerunLink>.
      </Alert>
    );
  } else if (rerunOfCheck) {
    return (
      <Alert status="info" className={className}>
        This check is a rerun of a previous check,{' '}
        <RerunLink rerun={rerunOfCheck}>view the previous check</RerunLink>.
      </Alert>
    );
  } else {
    return null;
  }
}
